/**
 * Convites de viagem — regras puras da tela, sem DOM.
 *
 * O servidor devolve a lista inteira, com todos os status, e nunca grava
 * `expired`: um convite pendente cujo prazo passou continua `pending` no
 * banco. É aqui que o prazo vira situação, e é por isso que tudo recebe o
 * relógio por parâmetro — a tela testa com o instante que quiser.
 */
import { daysLeftOnInvite } from '@nv/core';

import type { TripInviteView } from './api';

export type SituacaoDoConvite = 'pendente' | 'aceito' | 'revogado' | 'expirado';

export function situacaoDoConvite(convite: TripInviteView, agora: number): SituacaoDoConvite {
  if (convite.status === 'accepted') return 'aceito';
  if (convite.status === 'revoked') return 'revogado';
  if (convite.status === 'expired') return 'expirado';
  return Date.parse(convite.expiresAt) > agora ? 'pendente' : 'expirado';
}

/** Só os que ainda podem ser aceitos, do mais novo para o mais antigo. */
export function convitesPendentes(lista: readonly TripInviteView[], agora: number): TripInviteView[] {
  return lista
    .filter((c) => situacaoDoConvite(c, agora) === 'pendente')
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export function prazoDoConvite(expiresAt: string, agora: number): string {
  const fim = Date.parse(expiresAt);
  if (fim <= agora) return 'Expirado';
  const dias = daysLeftOnInvite(fim, agora);
  if (dias <= 1) {
    // Menos de 24 h: "amanhã" só se o dia civil for outro.
    return new Date(fim).getDate() === new Date(agora).getDate() ? 'Expira hoje' : 'Expira amanhã';
  }
  return `Expira em ${dias} dias`;
}

/**
 * Mensagem que sai pelo compartilhador do aparelho. O nome é o da viagem
 * aberta; nada aqui conhece viagem alguma.
 */
export function textoDeCompartilhamento(dados: { tripName: string; code: string; url: string }): string {
  return (
    `Entre na viagem "${dados.tripName}" no Viajandos: ${dados.url}\n` +
    `Se pedir um código, use ${dados.code}.`
  );
}

/** Quem gerencia participantes vai direto aos convites; quem não, ao grupo. */
export function rotaDeConvidar(podeGerenciar: boolean): string {
  return podeGerenciar ? '/grupo/convites' : '/grupo';
}

/** Rótulo curto do prazo do código do grupo: "vale até dd/mm, hh:mm". */
export function validadeDoCodigo(expiresAt: string, agora: number): string {
  const fim = Date.parse(expiresAt);
  if (fim <= agora) return 'Expirado';
  const d = new Date(fim);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `Vale até ${dd}/${mm}, ${hh}:${mi}`;
}

export const OPCOES_DE_VALIDADE: { valor: '1d' | '7d' | '30d' | 'trip'; rotulo: string }[] = [
  { valor: '1d', rotulo: '1 dia' },
  { valor: '7d', rotulo: '7 dias' },
  { valor: '30d', rotulo: '30 dias' },
  { valor: 'trip', rotulo: 'Até o fim da viagem' },
];
